import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { apiClient, formatSAR, formatApiError } from "../lib/api";
import { Gavel, Clock, TrendingUp, Zap, Loader2, ShieldCheck, User } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
// Auction bidding widget — displayed inside VehicleDetailPage when
// `vehicle.listing_type === "auction"`. Handles: countdown, min-bid
// calculation, bid submission, buy-now, and live bids feed.
// ─────────────────────────────────────────────────────────────────────
export default function AuctionBidBox({ vehicle, onBidPlaced }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [bids, setBids] = useState([]);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [now, setNow] = useState(Date.now());

  const current = Number(vehicle.current_bid_sar || 0);
  const starting = Number(vehicle.starting_bid_sar || vehicle.price_sar || 0);
  const increment = Number(vehicle.min_bid_increment_sar || 500);
  const buyNow = Number(vehicle.buy_now_price_sar || 0);
  const minRequired = current > 0 ? current + increment : starting;
  const isSeller = user?.id === vehicle.seller_id;
  const ended = vehicle.auction_status !== "live";

  // Load recent bids
  useEffect(() => {
    let cancelled = false;
    apiClient.get(`/auctions/${vehicle.id}/bids`)
      .then(({ data }) => { if (!cancelled) setBids(data.items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [vehicle.id, vehicle.bid_count]);

  // Countdown tick
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const timeLeft = useMemo(() => {
    if (!vehicle.auction_ends_at) return null;
    const end = new Date(vehicle.auction_ends_at).getTime();
    const diff = end - now;
    if (diff <= 0) return { ended: true };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    return { days, hours, mins, secs, ended: false };
  }, [now, vehicle.auction_ends_at]);

  const submit = async (targetAmount) => {
    setErr("");
    if (!user) { window.location.href = `/login?next=/vehicle/${vehicle.id}`; return; }
    const n = Number(targetAmount);
    if (!n || n < minRequired) {
      setErr(lang === "ar"
        ? `يجب أن تكون المزايدة ${minRequired.toLocaleString()} ر.س على الأقل.`
        : `Bid must be at least ${minRequired.toLocaleString()} SAR.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/auctions/${vehicle.id}/bid`, { amount_sar: n });
      setAmount("");
      if (onBidPlaced) onBidPlaced(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const buyNowAction = async () => {
    if (!buyNow) return;
    await submit(buyNow);
  };

  return (
    <div
      className="rounded-3xl p-6 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg,#0a0a0a 0%,#1a0a0a 50%,#3a0a0a 100%)", border: "2px solid #dc2626" }}
      data-testid="auction-bid-box"
    >
      <div className="absolute -top-10 -end-10 w-32 h-32 rounded-full opacity-20 blur-2xl" style={{ background: "#dc2626" }} />
      <div className="relative">
        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider" style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff" }}>
          <Gavel className="w-3.5 h-3.5" />
          {lang === "ar" ? "مزاد مباشر" : "Live auction"}
        </div>

        {/* Countdown */}
        <div className="mt-4 flex items-center gap-2" style={{ color: "#fca5a5" }}>
          <Clock className="w-4 h-4" />
          <div className="text-xs font-bold uppercase tracking-widest">
            {timeLeft?.ended || ended
              ? (lang === "ar" ? "انتهى المزاد" : "Auction ended")
              : (lang === "ar" ? "ينتهي خلال" : "Ends in")}
          </div>
        </div>
        {!ended && timeLeft && !timeLeft.ended && (
          <div className="mt-2 grid grid-cols-4 gap-2 max-w-md" data-testid="auction-countdown">
            {[
              { v: timeLeft.days, ar: "يوم", en: "days" },
              { v: timeLeft.hours, ar: "ساعة", en: "hrs" },
              { v: timeLeft.mins, ar: "دقيقة", en: "min" },
              { v: timeLeft.secs, ar: "ثانية", en: "sec" },
            ].map((u) => (
              <div key={u.en} className="rounded-xl p-2 text-center" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(220,38,38,0.35)" }}>
                <div className="font-display text-2xl font-bold" style={{ color: "#fecaca" }}>{String(u.v).padStart(2, "0")}</div>
                <div className="text-[9px] uppercase font-bold" style={{ color: "rgba(254,202,202,0.7)" }}>{lang === "ar" ? u.ar : u.en}</div>
              </div>
            ))}
          </div>
        )}

        {/* Current bid */}
        <div className="mt-6 flex items-baseline gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "rgba(254,202,202,0.7)" }}>
              {current > 0
                ? (lang === "ar" ? "أعلى مزايدة حالياً" : "Current highest bid")
                : (lang === "ar" ? "سعر البدء" : "Starting bid")}
            </div>
            <div className="font-display text-4xl font-bold" style={{ color: "#f7eecd" }}>
              {formatSAR(current || starting)} <span className="text-sm font-medium" style={{ color: "rgba(247,238,205,0.7)" }}>SAR</span>
            </div>
          </div>
          {current > 0 && vehicle.current_bid_user_name && (
            <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold" style={{ background: "rgba(201,165,92,0.20)", color: "#e8c873", border: "1px solid #c9a55c" }}>
              <User className="w-3 h-3" /> {vehicle.current_bid_user_name}
            </div>
          )}
        </div>

        {/* Bid input */}
        {!ended && !isSeller && (
          <div className="mt-5 flex flex-col sm:flex-row gap-2" data-testid="auction-bid-input-row">
            <div className="flex-1">
              <input
                type="number"
                min={minRequired}
                step={increment}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={lang === "ar" ? `أدخل ${minRequired.toLocaleString()} ر.س أو أكثر` : `Enter ${minRequired.toLocaleString()} SAR or more`}
                className="w-full px-4 py-3 rounded-xl text-lg font-display"
                style={{ background: "rgba(255,255,255,0.06)", color: "#f7eecd", border: "1.5px solid rgba(220,38,38,0.4)" }}
                data-testid="auction-bid-input"
              />
            </div>
            <button
              type="button"
              onClick={() => submit(amount)}
              disabled={busy || !amount}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-sm shadow-xl transition-transform hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff" }}
              data-testid="auction-place-bid-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
              {lang === "ar" ? "قدّم عرضك" : "Place bid"}
            </button>
          </div>
        )}
        {isSeller && !ended && (
          <div className="mt-5 text-sm p-3 rounded-xl" style={{ background: "rgba(201,165,92,0.15)", color: "#f7eecd", border: "1px solid rgba(201,165,92,0.4)" }}>
            {lang === "ar" ? "لا يمكنك المزايدة على إعلانك — هذا مزادك الخاص." : "You can't bid on your own listing — this is your auction."}
          </div>
        )}

        {/* Buy-now */}
        {!ended && !isSeller && buyNow > 0 && (
          <button
            type="button"
            onClick={buyNowAction}
            disabled={busy}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#d4b56e,#c9a55c,#8a6f2e)", color: "#0a0a0a", border: "1px solid #c9a55c" }}
            data-testid="auction-buy-now-btn"
          >
            <Zap className="w-4 h-4" />
            {lang === "ar" ? `اشترِ الآن — ${formatSAR(buyNow)} ر.س` : `Buy now — ${formatSAR(buyNow)} SAR`}
          </button>
        )}

        {err && (
          <div className="mt-3 px-4 py-2 rounded-xl text-sm bg-red-500/15 text-red-200 border border-red-500/30" data-testid="auction-bid-error">
            {err}
          </div>
        )}

        {/* Hint about escrow protection */}
        <div className="mt-5 inline-flex items-start gap-2 text-[11px]" style={{ color: "rgba(247,238,205,0.7)" }}>
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#c9a55c" }} />
          <span>{lang === "ar"
            ? "الفائز سيتلقى إشعاراً تلقائياً ولديه 24 ساعة لإتمام الدفع. يمكن اختيار ضمان كارتال (150 ر.س ثابتة) لحماية إضافية."
            : "The winner is auto-notified and has 24 hours to complete payment. Optional CARTAL Escrow (flat 150 SAR) available for protection."}
          </span>
        </div>

        {/* Recent bids feed */}
        {bids.length > 0 && (
          <div className="mt-6" data-testid="auction-bids-feed">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: "rgba(254,202,202,0.7)" }}>
              {lang === "ar" ? "آخر المزايدات" : "Recent bids"}
            </div>
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {bids.slice(0, 10).map((b, i) => (
                <div key={b.id} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-xs" style={{ background: i === 0 ? "rgba(201,165,92,0.15)" : "rgba(255,255,255,0.04)" }}>
                  <div className="inline-flex items-center gap-2" style={{ color: i === 0 ? "#f7eecd" : "rgba(247,238,205,0.8)" }}>
                    <User className="w-3 h-3" />
                    <span className="font-medium truncate max-w-[140px]">{b.user_name}</span>
                    {i === 0 && (
                      <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded" style={{ background: "#c9a55c", color: "#0a0a0a" }}>
                        {lang === "ar" ? "الأعلى" : "Top"}
                      </span>
                    )}
                  </div>
                  <div className="font-display font-bold" style={{ color: i === 0 ? "#f7eecd" : "rgba(247,238,205,0.85)" }}>
                    {formatSAR(b.amount_sar)} <span className="text-[10px] font-normal" style={{ color: "rgba(247,238,205,0.6)" }}>SAR</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
